import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import moment from 'moment';
import classNames from 'classnames';
import style from './style.module.scss';
import {IProps, ISelectorListProps, ITableSeaterParams} from './types';
import {RepeatBookingList} from './RepeatBookingList';
import {Button, createStyles, Grid, makeStyles, TextField, Theme, Typography} from '@material-ui/core';
import {getServiceOptions, getTableOptions, makeBookingTemplate} from './helper';
import {
  IRecurringBookingDetail,
  IRecurringBookingRequest,
  IRecurringBookingSaveRequest,
  IRecurringBookingSubset,
  RecurringBookingType, IWeeklyScheduleSubset
} from 'shared-services/recurring-booking-service/type';
import DateUtilsService from "external-services/date-utils-service";
import {cloneDeep, isEmpty} from "lodash-es";
import RecurringTableSeaterService from "shared-services/recurring-booking-service/RecurringTableSeater.service";
import {renderIf} from "external-services/react-utils-service";
import CircularProgress from "@material-ui/core/CircularProgress";
import {CheckCircle, Warning} from "@material-ui/icons";
import DatePickerOverlay from 'external-components/date-picker-overlay/index';
import {SelectorList} from "app/bookings/new/RepeatBooking/SelectorList";
import RecurringBookingService from 'shared-services/recurring-booking-service';

const NS = 'RepeatBooking';

const WEEKS = 'weeks';
const DAYS = 'days';
const MONTH = 'months';
/** Render recurring booking form */
export default function RepeatBooking({
  serviceViewList, bookingInfo, bookingDate, tableList, sectionsLookup, customerInfo, classPrefix,
  resetRecurringBookings, updateRecurringBookings, tableSeaterRecurringParams, preferredSectionId, //updatedBooking
  venueTimezoneId
}: IProps) {

  const customStyles = createStyles({
    root: {
      [`& .${classPrefix}-MuiInputBase-input`]: {
        width: 35,
        height: 8,
        padding: '6px 5px',
        fontSize: '16px',
        marginTop: '-2px'
      }
    },
    repeatTime: {
      marginLeft: 15
    },
    repeatDate: {
      [`& .${classPrefix}-MuiInputBase-root`]: {
        margin: '0px 0px 0px 5px'
      },
      [`& .${NS}-.MuiInputBase-input`]: {
        padding: '0 0 3px'
      },
      marginTop: -10
    },
    btnLoad: {
      [`& .${classPrefix}-.MuiButton-root`]: {
        padding: '4px 10px'
      },
      [`& .${classPrefix}-.MuiInput-root`]: {
        margin: '-10px 5px 0'
      },
      display: 'flex',
      alignItems: 'center'
    },
    bookingDate: {
      [`& .${classPrefix}-MuiInputBase-root`]: {
        margin: '2px 0px 0px 10px'
      },
      [`& .${classPrefix}-MuiInputBase-input`]: {
        width: 150,
        padding: '6px 0 3px'
      },
      marginTop: -10
    },
    recurringBookingList: {
      marginTop: 35
    }
  });

  const useStyles = makeStyles((theme: Theme) => customStyles);
  const classes = useStyles();
  const tableOptions = useRef<ISelectorListProps>(null);
  const serviceOptions = useRef<ISelectorListProps>(null);
  const repeatTimesValue = useRef<HTMLInputElement>(null);
  const repeatFrequencyValue = useRef<HTMLInputElement>(null);
  const [recurringBookingList, setRecurringBookingList] = useState<IRecurringBookingDetail[]>([]);
  const [repeatEndDate, setRepeatEndDate] = useState<string>(moment(new Date()).format('YYYY-MM-DD'));
  const [tableSeaterParamVal, setTableSeaterParams] = useState<ITableSeaterParams>(tableSeaterRecurringParams);
  const [originalTableProps, setOriginalTableProps] = useState<ITableSeaterParams>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expiredTimeMessage, showExpiredTimeMessage] = useState<boolean>(false);
  const [disableDone, setDoneDisable] = useState<boolean>(false);
  const [selectedRecurringType, setRecurringType] = useState<RecurringBookingType>(RecurringBookingType.Weekly);
  const [weeklySchedule, setWeeklySchedule] = useState<IWeeklyScheduleSubset[]>([]);
  const [tableSeaterRecurringParamsClones, setTableSeaterRecurringParamsClones] = useState<ITableSeaterParams[]>([]);
  useEffect(() => {
    if (recurringBookingList.length > 0) {
      const tempRecurringParams: ITableSeaterParams[] = [];
      recurringBookingList.forEach(() => {
        tempRecurringParams.push(cloneDeep(tableSeaterParamVal));
      })
      setTableSeaterRecurringParamsClones(tempRecurringParams);
    }
  }, [tableSeaterRecurringParams, recurringBookingList]);

  useEffect(() => {
    if (!originalTableProps) {
      setOriginalTableProps(cloneDeep(tableSeaterRecurringParams));
    }
  }, [])

  useEffect(() => {
    // Minimum 2 recurring bookings must be created
    repeatTimesValue.current.value = "2";
    const date: moment.Moment = moment(bookingDate);
    const unit = selectedRecurringType === RecurringBookingType.Weekly ? WEEKS : DAYS;
    const endDate: string = date.add(unit, 1).format('YYYY-MM-DD');
    if (selectedRecurringType === RecurringBookingType.Daily) {
      setRepeatEndDate(endDate);
    } else {
      repeatFrequencyValue.current.value = "1";
      setRepeatEndDate(endDate);

    }
  }, [selectedRecurringType])

  /** Assign default values, when bookingDate, tablelist or service is changed */
  useEffect(() => {
    // Clear the existing recurring bookings being displayed
    setRecurringBookingList([]);

    // Minimum 2 recurring bookings must be created
    repeatTimesValue.current.value = "2";

    if (selectedRecurringType === RecurringBookingType.Weekly) {
      // Repeat a booking every 1 week by default
      repeatFrequencyValue.current.value = "1";
    }

    const date: moment.Moment = moment(bookingDate);
    const unit = selectedRecurringType === RecurringBookingType.Weekly ? WEEKS : DAYS
    // Add 1 week (i.e. 7 days) or 1 day if daily in the future to the selected booking date.
    const endDate: string = date.add(unit, 1).format('YYYY-MM-DD');
    setRepeatEndDate(endDate);

    // Populate table list
    tableOptions.current = getTableOptions(tableList, bookingInfo, sectionsLookup);

  }, [bookingDate, tableList, bookingInfo.serviceId, selectedRecurringType]);

  /** Update the repeat end date */
  const onRepeatTimeChange = (input: string) => {
    // remove other value, only number can pass
    const intValue: number = parseInt(input.replace('/[^\d]/g', ''));
    // Calculate the number of weekly occurences for each booking using the repeat frequency
    const interval: number = selectedRecurringType === RecurringBookingType.Weekly ?
      parseInt(repeatFrequencyValue.current.value) * (intValue - 1) : intValue - 1;

    const unit = selectedRecurringType === RecurringBookingType.Weekly ? WEEKS : DAYS;
    // Add the calculated the weeks to the booking date
    const newEndDate: moment.Moment = moment(bookingDate).add(unit, interval)
    if (selectedRecurringType === RecurringBookingType.Weekly) {
      if (newEndDate < moment(bookingDate).add(6, MONTH)) {
        const repeatEndDate: string = newEndDate.format('YYYY-MM-DD');
        repeatTimesValue.current.value = intValue + '';
        setRepeatEndDate(repeatEndDate);
      } else {
        setRepeatTime(moment(bookingDate).add(6, MONTH).format('YYYY-MM-DD'));
      }
    } else {
      if (newEndDate < moment(bookingDate).add(14, DAYS)) {
        const repeatEndDate: string = newEndDate.format('YYYY-MM-DD');
        repeatTimesValue.current.value = intValue + '';
        setRepeatEndDate(repeatEndDate);
      } else {
        setRepeatTime(moment(bookingDate).add(14, DAYS).format('YYYY-MM-DD'));
      }
    }

  }

  /** Update the repeat time */
  const onRepeatDateChange = (selectedDate: string) => {
    if (selectedRecurringType === RecurringBookingType.Weekly) {
      if (moment(selectedDate) < moment(bookingDate).add(6, MONTH)) {
        setRepeatEndDate(selectedDate);
        setRepeatTime(selectedDate);
      } else {
        setRepeatEndDate(moment(bookingDate).add(6, MONTH).format('YYYY-MM-DD'));
        setRepeatTime(moment(bookingDate).add(6, MONTH).format('YYYY-MM-DD'));
      }
    } else {
      if (moment(selectedDate) < moment(bookingDate).add(14, DAYS)) {
        setRepeatEndDate(selectedDate);
        setRepeatTime(selectedDate);
      } else {
        setRepeatEndDate(moment(bookingDate).add(14, DAYS).format('YYYY-MM-DD'));
        setRepeatTime(moment(bookingDate).add(14, DAYS).format('YYYY-MM-DD'));
      }
    }
  }

  // Modify the number of weekly occurences
  const setRepeatTime = (date: string) => {
    let intValue = 1
    if (selectedRecurringType === RecurringBookingType.Weekly) {
      intValue = parseInt(repeatFrequencyValue.current.value.replace('/[^\d]/g', ''))
      repeatFrequencyValue.current.value = intValue + '';
    }
    const unit = selectedRecurringType === RecurringBookingType.Weekly ? WEEKS : DAYS;
    const numberOfWeeks: number = moment(date).diff(moment(bookingDate).startOf(DAYS), unit);
    let repeatTime = Math.floor(numberOfWeeks / intValue) + 1;
    if (repeatTime < 0) {
      repeatTime = 0;
    }
    repeatTimesValue.current.value = (repeatTime).toString();
  }

  const getRecurringBookings = async () => {
    setIsLoading(true);
    // Clear the existing recurring bookings being displayed
    setRecurringBookingList([]);
    setDoneDisable(false)
    const isTimeExpired = RecurringBookingService.checkForExpiredTime(DateUtilsService.onlyChangeTimeZone(bookingInfo.time, venueTimezoneId), moment());
    if (!isTimeExpired) {
      showExpiredTimeMessage(false);
      const requestParameters = makeRecurringBookingRequest();

      console.log('selectedRecurringType', selectedRecurringType)
      let weeklySchedule;
      if (selectedRecurringType === RecurringBookingType.Daily) {
        weeklySchedule = await RecurringBookingService.getWeeklySchedules();
        setWeeklySchedule(weeklySchedule)
      }
      const result = await RecurringBookingService.GenerateRecurringBookings(requestParameters);
      // Populate service list
      serviceOptions.current = getServiceOptions(bookingInfo.serviceId, serviceViewList);


      // setUpdatedBooking({} as IRecurringBookingSubset);
      // Calling table Picker API to get the Table Picker Params
      const time = DateUtilsService.DateToDateTimeString(bookingInfo.time);

      tableSeaterRecurringParams.props = await RecurringTableSeaterService.getTableSeaterInfo(originalTableProps.props, bookingInfo.serviceId, time, bookingInfo.people);
      setTableSeaterParams(tableSeaterRecurringParams);

      const recurringLst = result.map((r, index) => {
        r.hasSelected = true;
        r.booking.index = index;
        return r;
      })
      setRecurringBookingList(recurringLst);
      setIsLoading(false);
    } else {
      showExpiredTimeMessage(true);
      setIsLoading(false);
    }

  }

  // Populate recurring booking request
  const makeRecurringBookingRequest = () => {

    const bookingRequest: IRecurringBookingRequest = {
      recurringBooking: {
        customer: customerInfo,
        bookingRepeat: selectedRecurringType,
        repeatTimes: parseInt(repeatTimesValue.current.value),
        repeatFrequency: selectedRecurringType === RecurringBookingType.Weekly ? parseInt(repeatFrequencyValue.current.value) : 1,
        startTime: DateUtilsService.DateToDateTimeString(bookingInfo.time),
        endTime: moment(repeatEndDate).format('YYYY-MM-DDTHH:mm:ss')
      },
      bookingTemplate: makeBookingTemplate(bookingInfo, sectionsLookup, serviceViewList)
    }

    return bookingRequest;
  }


  const updateRecurringBookingsValue = (bookingList: IRecurringBookingDetail[]) => {
    const recurringBookingSaveRequest: IRecurringBookingSaveRequest = {
      customer: customerInfo,
      bookingRepeat: selectedRecurringType,
      repeatTimes: parseInt(repeatTimesValue.current.value),
      repeatFrequency: selectedRecurringType === RecurringBookingType.Weekly ? parseInt(repeatFrequencyValue.current.value) : 1,
      startTime: DateUtilsService.DateToDateTimeString(bookingInfo.time),
      endTime: !isEmpty(bookingList) ? bookingList.slice(-1)[0].booking.time : '',
      bookingList
    }
    updateRecurringBookings(recurringBookingSaveRequest)
    setDoneDisable(true);
  }

  /** Update the checkbox check/uncheck state of the selected booking */
  const updateRecurringBookingSelection = (index: number, isChecked: boolean) => {
    recurringBookingList[index].hasSelected = isChecked;
    setRecurringBookingList(recurringBookingList);
    // updateRecurringBookingsValue(recurringBookingList.filter(r => r.hasSelected).slice(1)); // Remove the 1st booking from the list - that is always the original booking
  }

  /** Update the valid/invalid state of the selected booking to display the icon i.e. green tick or cancel */
  const updateRecurringBookingValidity = (index: number, isBookingValid: boolean, bookingInfo: IRecurringBookingSubset) => {
    recurringBookingList[index].isValid = isBookingValid;
    recurringBookingList[index].booking = bookingInfo;
    setRecurringBookingList(recurringBookingList);
    setDoneDisable(false);
    // updateRecurringBookingsValue(recurringBookingList.filter(r => r.hasSelected).slice(1)); // Remove the 1st booking from the list - that is always the original booking
  }

  return (
    <div className={style.repeatBookingTabContent}>
      <Grid container>
        <Grid item xs={2} data-testid="recurring-type-dropdown" >
          <SelectorList
            classPrefix={classPrefix}
            selectedOption={selectedRecurringType}
            onSelectedOptionChange={(value) => {
              setRecurringType(value as unknown as RecurringBookingType)
            }}
            listOfOptions={[{name: RecurringBookingType.Daily, value: RecurringBookingType.Daily},
              {name: RecurringBookingType.Weekly, value: RecurringBookingType.Weekly}]}/>
        </Grid>
        <Grid item xs={2} className={style.recurringRadioButtonPanel}>
          <p className={style.recurringRadioButtonLabel}>Repeat</p>
          <div className={classNames({
            [classes.root]: true,
            [classes.repeatTime]: true
          })}>
            <TextField inputRef={repeatTimesValue} type="number" variant="standard"
                       InputProps={{inputProps: {min: 1}}}
                       onChange={(e: ChangeEvent<HTMLInputElement>) => onRepeatTimeChange(e.target.value)}/> times
          </div>
        </Grid>
        <Grid item xs={3} className={style.recurringRadioButtonPanel}>
          <p className={style.recurringRadioButtonLabel}>Until</p>
          <div className={classes.bookingDate}
               data-testid="recurring-type-date-picker">
            <DatePickerOverlay
              maxDate={selectedRecurringType === RecurringBookingType.Weekly ? moment(bookingDate).add(6, MONTH) : moment(bookingDate).add(14, 'day')}
              currentDate={moment(repeatEndDate)}
              handleDateUpdate={date => onRepeatDateChange(date.format('YYYY-MM-DD'))}
              disableFutureDate={false}
              disablePastDate={true}
            />
          </div>

        </Grid>
        <Grid item xs={3} className={style.recurringRadioButtonPanel}>
          <div className={classes.root} data-testid="repeat-frequency-text">
            Repeat
            {renderIf(selectedRecurringType === RecurringBookingType.Weekly, () => (
              <>
                <span>&nbsp;every</span>
                <TextField className={classes.repeatTime} inputRef={repeatFrequencyValue} type="number"
                           variant="standard"
                           InputProps={{inputProps: {min: 1}}}
                           onChange={() => setRepeatTime(repeatEndDate)}/> week/s
              </>
            ))}
            {renderIf(selectedRecurringType === RecurringBookingType.Daily, () => (
              <span>&nbsp;Daily</span>
            ))}

          </div>
        </Grid>
        <Grid item xs={1}>
          <Button data-testid="load-recurring" variant="contained" color="primary"
                  onClick={getRecurringBookings}>Load</Button>
        </Grid>
        {
          recurringBookingList.length > 0 &&
          <Grid item xs={1}>
            {/*todo add Unit tests*/}
            <Button data-testid="rest-recurring" variant="contained" color="primary" onClick={() => {
              setRecurringBookingList([]);
              resetRecurringBookings()
            }}>Reset</Button>
          </Grid>
        }
      </Grid>
      {renderIf(expiredTimeMessage, () => (
        <div>
          <Typography variant="h4" className={style.msg}>
            The selected time has expired.
          </Typography>
        </div>
      ))}

      {renderIf(isLoading, () => (
        <div>
          <CircularProgress size={50} className={style.nextLoader}/>
        </div>
      ))}
      <div className={classes.recurringBookingList}>
        {
          !isLoading && recurringBookingList.map((item, index) =>
            <div key={index} data-testid="recurring-list">
              <RepeatBookingList
                classPrefix={classPrefix}
                bookingIndex={index}
                bookingDetail={item}
                serviceListOptions={serviceOptions.current}
                weeklySchedule={weeklySchedule}
                tableListOptions={tableOptions.current}
                // updatedBooking={updatedBookingVal}
                tableSeaterRecurringParams={tableSeaterRecurringParamsClones[index]}
                preferredSectionId={preferredSectionId}
                onBookingSelection={updateRecurringBookingSelection}
                onBookingChange={updateRecurringBookingValidity}/>
            </div>)
        }
      </div>

      <div>
        {
          recurringBookingList.length > 0 &&
          <div className={style.done}>
            <Button variant="contained" color="primary" data-testid="done-recurring-list"
                    disabled={disableDone}
                    onClick={() => {
                      updateRecurringBookingsValue(recurringBookingList.slice(1).filter(r => r.hasSelected && r.isValid)); // Remove the 1st booking from the list - that is always the original booking
                    }}>Done
            </Button>
            <div className={style.doneIcon}>{disableDone ? <CheckCircle className={style.fillColorSuccess}/> :
              <Warning className={style.fillColorWarning}/>}</div>
            <Typography data-testid="done-recurring-list-message"
                        className={classNames({
                          [style.doneMessage]: true,
                          [style.doneMessageFailure]: !disableDone,
                          [style.doneMessageSuccess]: disableDone,
                        })}>
              {disableDone ? `The recurring bookings are saved` : `Click Done to complete and save recurring bookings`}
            </Typography>

          </div>
        }
      </div>
    </div>
  )
}
